import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap, catchError, shareReplay } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { ApiResponse } from '../shared/models';

export interface KAIPromptDto {
  promptKey: string;
  promptValue: string;
  constraints?: string;
  description?: string;
  module: string;
  outputFormat: string;
  isActive: boolean;
  currentVersion: number;
  createdDate: string;
  modifiedDate?: string;
  modifiedBy?: number;
  modifiedByName?: string;
}

export interface KAIPromptVersionDto {
  versionId: number;
  promptKey: string;
  promptValue: string;
  constraints?: string;
  versionNumber: number;
  changedBy: number;
  changedByName?: string;
  changedDate: string;
  changeNote?: string;
}

export interface UpdatePromptRequest {
  promptKey: string;
  promptValue: string;
  constraints?: string;
  changeNote?: string;
}

export interface RollbackPromptRequest {
  promptKey: string;
  versionId: number;
}

export interface TestPromptRequest {
  systemPrompt: string;
  testMessage: string;
  maxTokens?: number;
}

export interface PromptWorkshopRequest {
  operation: string;
  currentPrompt?: string;
  context?: string;
  outputFormat?: string;
  module?: string;
}

export interface PromptWorkshopResponse {
  generatedText?: string;
  tokensUsed: number;
  estimatedCost: number;
}

@Injectable({
  providedIn: 'root'
})
export class KaiPromptService {
  private get apiUrl(): string {
    return `${this.configService.get('apiBaseUrl')}/KAIPrompt`;
  }

  // Runtime prompt cache: key → { value, expiry }
  private promptCache = new Map<string, { value: string; expiry: number }>();
  private static readonly CACHE_TTL_MS = 30 * 60 * 1000; // 30 min

  constructor(
    private http: HttpClient,
    private configService: ConfigService
  ) {}

  // ═══════════════════════════════════════════════
  // Admin endpoints (used by kai-prompts component)
  // ═══════════════════════════════════════════════

  getPrompts(): Observable<ApiResponse<KAIPromptDto[]>> {
    return this.http.get<ApiResponse<KAIPromptDto[]>>(`${this.apiUrl}/GetPrompts`).pipe(
      map(response => response)
    );
  }

  getPrompt(key: string): Observable<ApiResponse<KAIPromptDto>> {
    return this.http.get<ApiResponse<KAIPromptDto>>(`${this.apiUrl}/GetPrompt/${encodeURIComponent(key)}`).pipe(
      map(response => response)
    );
  }

  updatePrompt(request: UpdatePromptRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.apiUrl}/UpdatePrompt`, request).pipe(
      tap(response => {
        if (response?.success) {
          this.promptCache.delete(request.promptKey);
        }
      })
    );
  }

  getHistory(key: string): Observable<ApiResponse<KAIPromptVersionDto[]>> {
    return this.http.get<ApiResponse<KAIPromptVersionDto[]>>(`${this.apiUrl}/GetHistory/${encodeURIComponent(key)}`).pipe(
      map(response => response)
    );
  }

  rollback(request: RollbackPromptRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.apiUrl}/Rollback`, request).pipe(
      tap(response => {
        if (response?.success) {
          this.promptCache.delete(request.promptKey);
        }
      })
    );
  }

  resetToDefault(promptKey: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.apiUrl}/ResetToDefault`, { promptKey }).pipe(
      tap(response => {
        if (response?.success) {
          this.promptCache.delete(promptKey);
        }
      })
    );
  }

  testPrompt(request: TestPromptRequest): Observable<ApiResponse<PromptWorkshopResponse>> {
    return this.http.post<ApiResponse<PromptWorkshopResponse>>(`${this.apiUrl}/TestPrompt`, request).pipe(
      map(response => response)
    );
  }

  workshop(request: PromptWorkshopRequest): Observable<ApiResponse<PromptWorkshopResponse>> {
    return this.http.post<ApiResponse<PromptWorkshopResponse>>(`${this.apiUrl}/Workshop`, request).pipe(
      map(response => response)
    );
  }

  // ═══════════════════════════════════════════════
  // Runtime prompt fetching (used by feature components)
  // ═══════════════════════════════════════════════

  /**
   * Get a prompt value by key with local caching (30 min TTL).
   * Falls back to the provided default if fetch fails.
   */
  getPromptValue(key: string, fallback: string): Observable<string> {
    const cached = this.promptCache.get(key);
    if (cached && cached.expiry > Date.now()) {
      return of(cached.value);
    }

    return this.getPrompt(key).pipe(
      map(response => {
        if (response?.success && response.result?.promptValue) {
          const composed = response.result.constraints
            ? `${response.result.constraints}\n\n---\n\n${response.result.promptValue}`
            : response.result.promptValue;
          this.promptCache.set(key, { value: composed, expiry: Date.now() + KaiPromptService.CACHE_TTL_MS });
          return composed;
        }
        // Cache fallback on failed response to prevent repeated API calls
        this.promptCache.set(key, { value: fallback, expiry: Date.now() + KaiPromptService.CACHE_TTL_MS });
        return fallback;
      }),
      catchError(() => {
        // Cache fallback on API error to prevent repeated failed requests
        this.promptCache.set(key, { value: fallback, expiry: Date.now() + KaiPromptService.CACHE_TTL_MS });
        return of(fallback);
      })
    );
  }
}
